import {
  CompanyStructureBase,
  CompanyStructureKey,
  companyStructureSchema,
  SegmentType,
} from '@api/models'
import useFields from '@hooks/useFields'
import { getInputPropsForModalForms } from '@utils/getInputPropsForModalForms'
import { validateAndSubmit } from '@utils/validateAndSubmit'
import { useState, useEffect } from 'react'
import { FormSubmitEvent } from 'types'

interface SegmentFormProps {
  initialValues: CompanyStructureBase
  handleSubmit: (payload: CompanyStructureBase) => Promise<void>
  selectedStructureType?: SegmentType
  existingTypes?: SegmentType[]
}

export const useSegmentTypeForm = ({
  initialValues,
  handleSubmit,
  selectedStructureType,
  existingTypes,
}: SegmentFormProps) => {
  const { fields } = useFields()
  const [form, setForm] = useState({
    values: initialValues,
    errors: {},
  })

  function getField(name: CompanyStructureKey) {
    return getInputPropsForModalForms({
      name,
      fieldMap: fields,
      schema: companyStructureSchema,
      initialValues,
      form,
      setForm,
    })
  }

  async function handleFormSubmit(e: FormSubmitEvent): Promise<void> {
    e.preventDefault()

    const proposedName = (form?.values?.KY_DESC ?? '').trim().toLowerCase()
    if (proposedName && Array.isArray(existingTypes) && existingTypes.length > 0) {
      const isDuplicate = existingTypes.some(
        t => (t?.label ?? '').trim().toLowerCase() === proposedName && t?.value !== selectedStructureType?.value
      )
      if (isDuplicate) {
        setForm(prev => ({
          ...prev,
          errors: {
            ...prev.errors,
            KY_DESC: 'A segment type with this name already exists',
          },
        }))
        return
      }
    }
    await validateAndSubmit(
      companyStructureSchema,
      form,
      setForm,
      handleSubmit,
      (err: any) => {
        const msg =
          err?.data?.validation_errors?.[0] ||
          err?.response?.data?.validation_errors?.[0] ||
          'There was an error'
        setForm(prev => ({
          ...prev,
          errors: { ...prev.errors, KY_DESC: msg },
        }))
      }
    )
  }

  useEffect(() => {
    setForm({
      values: {
        value: selectedStructureType?.value,
        KY_DESC: initialValues.KY_DESC,
      },
      errors: {},
    })
  }, [selectedStructureType])

  return {
    getField,
    handleFormSubmit,
    form,
  }
}
